import { ApiError, guard404, request } from "./client";
import type { components } from "./schema";
import { apiGet, apiPost } from "./typed";

// Mirrors pkg/forge: the OUTBOUND forge-integration layer (connect a repo +
// auto-provision the inbound webhook + token binding). Distinct from
// api/webhooks.ts (the raw inbound-webhook CRUD).

// The forge enums the Go structs encode only as strings. We keep them as
// precise unions for provider-specific UI, then graft them onto the generated
// spec schemas below (Omit + &) so the FIELD SET stays spec-derived — a Go-side
// rename breaks the build — while these three fields stay narrowed.
export type ForgeProvider = "gitlab" | "github" | "forgejo";
export type ForgeKind = "oauth_app" | "github_app" | "pat";
export type ForgeConnectionStatus = "active" | "needs_reauth" | "revoked" | "degraded";

export type ForgeConnection = Omit<
  components["schemas"]["Connection"],
  "provider" | "kind" | "status"
> & {
  provider: ForgeProvider;
  kind: ForgeKind;
  status: ForgeConnectionStatus;
};

export type ForgeRepo = components["schemas"]["RepoSummary"];

export interface ForgeIntegration {
  id: string;
  connection_id: string;
  provider: ForgeProvider;
  repo_full_name: string;
  bot_ids: string[];
  events_normalized: string[];
  webhook_id: string;
  hook_id: string;
  hook_url?: string;
  managed_secret_id?: string;
  /** When true, the forge's issues are mirrored onto the team's native
   *  board (one-way forge→board sync). Toggled per-integration from the
   *  Integrations tab; absent on servers that predate the feature. */
  sync_issues_enabled?: boolean;
  /** Operator overrides stamped onto every run this integration launches
   *  (pkg/forge.RepoIntegration.LaunchVars) — durable across
   *  re-provisioning. The studio surfaces `review_tier`
   *  (glance/guard/audit, review-pr only) and `gate_enabled` (the merge-gate
   *  posture); any other key round-trips through updateForgeRepoBots
   *  unmodified. */
  launch_vars?: Record<string, string>;
  /** The repo's routing policy (pkg/llmroute, ADR-129) — the binding
   *  level for every run this integration launches, webhook and schedule
   *  alike. Hand-typed: the repo-bots routes carry no request/response
   *  schema in openapi.json, so this field regenerates nothing. */
  routing?: Record<string, unknown> | null;
  /** The per-repository bot iterion minted for this repo (a GitLab project
   *  access token) on a connection that posts as project bots: its login,
   *  its token's expiry and the last rotation refusal. Absent otherwise. */
  project_bot?: {
    login: string;
    token_id?: string;
    expires_at?: string;
    rotated_at?: string;
    rotation_error?: string;
    avatar_error?: string;
  } | null;
  created_at: string;
}

// ForgeTeamRepo is one row of the team-wide connected-repo aggregator
// (GET /api/teams/{id}/forge/repos) — the RepoSwitcher's data source: a
// RepoIntegration joined with its connection server-side.
export interface ForgeTeamRepo {
  connection_id: string;
  connection_status?: ForgeConnectionStatus;
  provider: ForgeProvider;
  repo_full_name: string;
  clone_url?: string;
  web_url?: string;
  integration_id: string;
  bot_ids: string[];
  sync_issues_enabled: boolean;
  /** True when a bot bound here declares the `statuses` forge scope — it
   *  posts a merge-gate commit status, so the repo has a posture to choose.
   *  OMITTED when the server could not resolve one of the row's bots: that
   *  is "unknown", never "no gate". */
  gates_merges?: boolean;
  /** The per-repository bot this repo's reviews are posted by, when iterion
   *  minted one (a GitLab project access token) — with its token's expiry
   *  and the last rotation refusal. Absent on a repo posting as its
   *  connection's account. */
  bot_login?: string;
  bot_token_expires_at?: string;
  bot_rotation_error?: string;
}

/** Stable identity of a connected repo across connections. */
export function forgeTeamRepoKey(r: Pick<ForgeTeamRepo, "connection_id" | "repo_full_name">): string {
  return `${r.connection_id}::${r.repo_full_name}`;
}

export async function listTeamForgeRepos(teamID: string): Promise<ForgeTeamRepo[]> {
  const r = await guard404("forge_integrations", () =>
    request<{ repos: ForgeTeamRepo[] }>(`/teams/${teamID}/forge/repos`),
  );
  return r.repos ?? [];
}

// ForgeConnectionHealth is the connection's actionable live state — for a
// GitHub App it carries the installation's real repo scope and the GitHub
// settings URL where the scope/permissions can be widened.
export type ForgeConnectionHealth = components["schemas"]["forgeConnectionHealth"];

export async function getForgeConnectionHealth(
  teamID: string,
  connID: string,
): Promise<ForgeConnectionHealth> {
  return (await apiGet("/api/teams/{id}/forge/connections/{conn_id}/health", {
    params: { id: teamID, conn_id: connID },
  })) as ForgeConnectionHealth;
}

// ForgeAvatarResult is what the apply-avatar action returns: the refreshed
// connection (avatar_applied_at / avatar_error) and, when the forge reports
// one, the new avatar's URL.
export type ForgeAvatarResult = Omit<components["schemas"]["forgeAvatarResp"], "connection"> & {
  connection?: ForgeConnection;
};

// applyForgeConnectionAvatar uploads the iterion-bot avatar onto the account
// behind a PAT connection. The server refuses an OAuth connection (a person's
// account) and GitHub (no API — its error names where to upload by hand); an
// account the forge does not flag as a bot needs `force`, which the card asks
// the operator to confirm first.
export async function applyForgeConnectionAvatar(
  teamID: string,
  connID: string,
  input: { variant?: "plain" | "circle"; force?: boolean } = {},
): Promise<ForgeAvatarResult> {
  return (await apiPost("/api/teams/{id}/forge/connections/{conn_id}/avatar", {
    params: { id: teamID, conn_id: connID },
    body: input,
  })) as ForgeAvatarResult;
}

// createForgeRepo creates a NEW repository on a connected forge (the
// "new app → new repo" journey). Creation only — iterion never updates
// or deletes forge repositories.
export async function createForgeRepo(
  teamID: string,
  input: {
    connection_id: string;
    owner?: string;
    name: string;
    description?: string;
    private?: boolean;
    default_branch?: string;
    init_readme?: boolean;
  },
): Promise<{ repo: ForgeRepo; clone_url: string }> {
  return (await apiPost("/api/teams/{id}/forge/repos", {
    params: { id: teamID },
    body: input,
  })) as { repo: ForgeRepo; clone_url: string };
}

/** Counts returned by a manual issue-sync run (POST …/sync). */
export interface ForgeSyncResult {
  synced: number;
  created: number;
  updated: number;
}

export interface ForgeEnablePreview {
  events_normalized: string[];
  /** The forge's native event names the hook will subscribe to. */
  forge_native_events: string[];
  scopes: Record<string, string>;
  secrets: Array<{ bot_id: string; secret: string }>;
  /** Slash-commands the enabled bots add to the webhook (command → bot). */
  commands?: Array<{ command: string; bot_id: string }>;
  /** Who the bots post as. `project_bot` marks a connection that posts as a
   *  per-repository bot iterion mints at enable: `handle` is then that bot's
   *  login once it exists, and empty before the first enable. */
  identity: { handle: string; provider: string; base_url: string; project_bot?: boolean };
  /** True when a provisionable selected bot declares the `statuses` forge
   *  scope — the server's own anyBotGatesMerges predicate, the one that
   *  forces review_on_sync. The merge-gate posture is a choice only then. */
  gates_merges: boolean;
  /** The base the inbound hook URL will be built from: this connection's
   *  pinned `webhook_base_url`, else the deployment's public URL; no
   *  trailing slash. Optional because a server that predates the field
   *  reports none, which is not the same answer as "no public URL" (the
   *  empty string): a surface says nothing about an address it was not
   *  given. */
  hook_base_url?: string;
  /** Non-empty = a bot can't be auto-installed (neither forge: nor an
   *  invocation). */
  conflicts: string[];
}

export interface ForgeProvisionResult {
  integration_id: string;
  webhook_id: string;
  hook_id: string;
  managed_secret_id: string;
  bot_ids: string[];
  created: boolean;
  // Set (202) when the org requires an org admin's approval: nothing was
  // provisioned yet — the request is parked in the org approval queue.
  pending_approval?: boolean;
  approval_id?: string;
  detail?: string;
  // The blast-radius screen's output on a direct write: what the write
  // landed but could not gate (a WARN, not a refusal). Empty on a parked
  // request — the screen runs on the paths that write.
  warnings?: string[];
}

// ForgeOAuthApp is a per-tenant, per-instance OAuth application's credentials
// (client_id + sealed client_secret). The connect form offers OAuth for a
// (provider, instance) only when one of these exists for it. The field set is
// the spec's ForgeOAuthApp schema; provider stays a narrowed union. `installable`
// is true for a manifest-created GitHub App whose private key iterion holds (it
// can be INSTALLED — least-privilege github_app — not only OAuth-authorized).
export type ForgeOAuthApp = Omit<components["schemas"]["ForgeOAuthApp"], "provider"> & {
  provider: ForgeProvider;
};

export interface RegisterForgeOAuthAppInput {
  provider: ForgeProvider;
  forge_base_url?: string;
  /** "manual" pastes client_id+client_secret; "auto"/"auto_from_connection" call the forge API. */
  mode?: "manual" | "auto" | "auto_from_connection";
  client_id?: string;
  client_secret?: string;
  admin_token?: string;
  connection_id?: string;
  /** mode=manual: the hook base every connection made through this app
   *  inherits — for a forge that only delivers to allowlisted hosts. */
  webhook_base_url?: string;
}

export interface ConnectForgeInput {
  provider: ForgeProvider;
  mode: "oauth" | "pat" | "app";
  forge_base_url?: string;
  pat?: string;
  display_name?: string;
  /** Studio path to return to after an OAuth / App-install round-trip. */
  next?: string;
  /**
   * mode=app: which of the team's GitHub Apps to install, when it holds
   * several (one per owning org). Omitted = the team's single App for the host.
   */
  oauth_app_id?: string;
  /**
   * mode=oauth, GitLab only: "project_token" makes iterion mint one project
   * access token per enabled repository — its bot user posts the reviews,
   * the OAuth grant only administers. Omitted = bots post as the account.
   */
  bot_identity?: "project_token";
}

export interface ConnectForgeResult {
  connection?: ForgeConnection;
  /** Present for mode=oauth — the studio redirects the window here. */
  authorize_url?: string;
  /** Present for mode=app (GitHub) — the App install URL to redirect to. */
  install_url?: string;
}

export async function listForgeConnections(teamID: string): Promise<ForgeConnection[]> {
  const r = await guard404("forge_integrations", () =>
    apiGet("/api/teams/{id}/forge/connections", { params: { id: teamID } }),
  );
  return (r.connections ?? []) as ForgeConnection[];
}

export async function connectForge(
  teamID: string,
  input: ConnectForgeInput,
): Promise<ConnectForgeResult> {
  // Body type-checked against the spec's forgeConnectReq.
  return (await apiPost("/api/teams/{id}/forge/connections", {
    params: { id: teamID },
    body: input,
  })) as ConnectForgeResult;
}

// setForgeConnectionWebhookBase pins the base this connection's inbound hook
// URLs are built from — for a forge that cannot reach the deployment's public
// URL (a self-managed GitLab whose outbound allowlist names another host
// answers HTTP 422 to the hook registration). The EMPTY STRING clears the pin
// and hands the connection back to the public URL.
//
// Sent ALONE: the server refuses a body that also carries
// security_read_enabled, because the two are unrelated operations and nothing
// makes them atomic.
export async function setForgeConnectionWebhookBase(
  teamID: string,
  connID: string,
  base: string,
): Promise<ForgeConnection> {
  return request(`/teams/${teamID}/forge/connections/${connID}`, {
    method: "PATCH",
    body: JSON.stringify({ webhook_base_url: base }),
  });
}

// replaceForgeConnectionToken swaps a PAT connection's token IN PLACE
// (#2488) — the renewal path for a GitLab project/group access token, which
// must expire: Rotate on the forge, then paste the new value here. The server
// WhoAmIs it first and refuses one that belongs to another account, so the
// connection's hooks, enabled repos and managed secret survive the swap.
// Sent ALONE, like every field of this PATCH (the server refuses a combined
// body). The value is a write-only secret: it is never read back.
export async function replaceForgeConnectionToken(
  teamID: string,
  connID: string,
  pat: string,
): Promise<ForgeConnection> {
  return request(`/teams/${teamID}/forge/connections/${connID}`, {
    method: "PATCH",
    body: JSON.stringify({ pat }),
  });
}

// reauthorizeForgeConnection starts the OAuth re-authorisation of an EXISTING
// connection (#2698) — the repair for a needs_reauth (or degraded) oauth_app
// connection, which re-running the connect wizard would otherwise strand: a
// second connection next to the dead one that still owns every integration.
// The caller redirects the window to the returned authorize_url; the callback
// seals the fresh token pair into the same row and returns to `next`.
export async function reauthorizeForgeConnection(
  teamID: string,
  connID: string,
  next?: string,
): Promise<{ authorize_url: string }> {
  return request(`/teams/${teamID}/forge/connections/${connID}/reauthorize`, {
    method: "POST",
    body: JSON.stringify({ next }),
  });
}

export async function deleteForgeConnection(teamID: string, connID: string): Promise<void> {
  await request<void>(`/teams/${teamID}/forge/connections/${connID}`, { method: "DELETE" });
}

export async function listForgeRepos(
  teamID: string,
  connID: string,
  search?: string,
  page?: number,
): Promise<ForgeRepo[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (page) params.set("page", String(page));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const r = await request<{ repos: ForgeRepo[] }>(
    `/teams/${teamID}/forge/connections/${connID}/repos${qs}`,
  );
  return r.repos ?? [];
}

export async function listForgeIntegrations(teamID: string): Promise<ForgeIntegration[]> {
  const r = await guard404("forge_integrations", () =>
    request<{ integrations: ForgeIntegration[] }>(`/teams/${teamID}/forge/repo-bots`),
  );
  return r.integrations ?? [];
}

export async function previewForgeEnable(
  teamID: string,
  connID: string,
  repo: string,
  bots: string[],
): Promise<ForgeEnablePreview> {
  const params = new URLSearchParams({ connection_id: connID, repo, bots: bots.join(",") });
  return request(`/teams/${teamID}/forge/repo-bots/preview?${params.toString()}`);
}

// enableForgeRepoBots provisions a repo's bots on a connection.
//
// launchVars, when passed, pins operator overrides on the integration
// (`gate_enabled` for the merge-gate posture). OMITTED leaves the stored pins
// alone — and the server refuses a non-nil map that would drop keys the repo
// already pins, so a caller only passes one for a repo it is integrating for
// the first time; an already-integrated repo changes its pins through
// updateForgeRepoBots, over its whole launch_vars map.
export async function enableForgeRepoBots(
  teamID: string,
  connID: string,
  repo: string,
  botIDs: string[],
  scheduleCrons?: Record<string, string>,
  launchVars?: Record<string, string>,
): Promise<ForgeProvisionResult> {
  return request(`/teams/${teamID}/forge/repo-bots`, {
    method: "POST",
    body: JSON.stringify({
      connection_id: connID,
      repo,
      bot_ids: botIDs,
      schedule_crons:
        scheduleCrons && Object.keys(scheduleCrons).length > 0 ? scheduleCrons : undefined,
      launch_vars: launchVars,
    }),
  });
}

// updateForgeRepoBots patches one integration.
//
// botIDs, when passed, sets its EXACT bot set (replace semantics — the per-bot
// unbind); empty lists are rejected server-side, and removing the last bot is
// disableForgeIntegration. UNDEFINED omits `bot_ids`, which keeps the stored
// set (pkg/server forgeUpdateReq): a caller changing only the launch vars must
// pass undefined rather than echo a bot set read when its page loaded — a
// concurrent bind would be undone by that snapshot.
//
// launchVars, when passed, REPLACES the integration's whole launch_vars map
// server-side (forge.RepoIntegration.LaunchVars is not merged on PATCH) —
// callers that want to change one key must spread the integration's
// current launch_vars first (see RepoDetail's review tier and merge gate).
// A map that would drop a pin the repo already holds is REFUSED 400
// (launch_vars_drop) naming those pins, unless launchVarsReplace states the
// whole-map replacement explicitly: read the refusal with
// launchVarsRefusalOf and offer the gesture it names.
//
// routing, when passed, is the repo's routing policy (ADR-129): an object
// REPLACES the stored block wholesale, null CLEARS it, UNDEFINED omits the
// field so the stored block stands — the server's three-way rule. Every
// write is validated server-side (locks refused, literal values only) and
// parks on an org that requires provisioning approval.
export async function updateForgeRepoBots(
  teamID: string,
  integrationID: string,
  botIDs: string[] | undefined,
  scheduleCrons?: Record<string, string>,
  launchVars?: Record<string, string>,
  routing?: Record<string, unknown> | null,
  launchVarsReplace?: boolean,
): Promise<ForgeProvisionResult> {
  return request(`/teams/${teamID}/forge/repo-bots/${integrationID}`, {
    method: "PATCH",
    body: JSON.stringify({
      // JSON.stringify omits a key whose value is undefined, which is how
      // "keep the stored bot set" reaches the server.
      bot_ids: botIDs,
      schedule_crons:
        scheduleCrons && Object.keys(scheduleCrons).length > 0 ? scheduleCrons : undefined,
      launch_vars: launchVars,
      routing,
      // Only ever sent as `true`: the flag is a one-shot operator gesture
      // ("replace the whole map, I know which pins go"), so a `false` on
      // every ordinary write would read as a posture the operator never took.
      launch_vars_replace: launchVarsReplace ? true : undefined,
    }),
  });
}

export async function disableForgeIntegration(
  teamID: string,
  integrationID: string,
): Promise<void> {
  await request<void>(`/teams/${teamID}/forge/repo-bots/${integrationID}`, { method: "DELETE" });
}

// updateForgeIntegration patches a connected repo's integration — today
// just the issue-sync toggle. Returns the refreshed ForgeIntegration so the
// caller reflects the new `sync_issues_enabled` without a re-list.
export async function updateForgeIntegration(
  teamID: string,
  integrationID: string,
  patch: { sync_issues_enabled?: boolean },
): Promise<ForgeIntegration> {
  return request(`/teams/${teamID}/forge/integrations/${integrationID}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

// syncForgeIntegration triggers a one-shot forge→board issue sync and
// returns the counts (synced / created / updated) for the operator's toast.
export async function syncForgeIntegration(
  teamID: string,
  integrationID: string,
): Promise<ForgeSyncResult> {
  return request(`/teams/${teamID}/forge/integrations/${integrationID}/sync`, {
    method: "POST",
  });
}

// ForgeHook is a webhook registered on the forge side for an integration's
// repo. Mirrors the {id,url,events,active} shape the server normalizes from
// each provider's hook API — used to audit that the iterion webhook is still
// live on the forge.
export interface ForgeHook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
}

// listForgeIntegrationHooks reads the forge-side registered hooks for an
// integration's repo, so the operator can audit them against what iterion
// provisioned.
export async function listForgeIntegrationHooks(
  teamID: string,
  integrationID: string,
): Promise<ForgeHook[]> {
  const r = await request<{ hooks: ForgeHook[] }>(
    `/teams/${teamID}/forge/integrations/${integrationID}/hooks`,
  );
  return r.hooks ?? [];
}

export async function listForgeOAuthApps(teamID: string): Promise<ForgeOAuthApp[]> {
  const r = await guard404("forge_integrations", () =>
    apiGet("/api/teams/{id}/forge/oauth-apps", { params: { id: teamID } }),
  );
  return (r.apps ?? []) as ForgeOAuthApp[];
}

export async function registerForgeOAuthApp(
  teamID: string,
  input: RegisterForgeOAuthAppInput,
): Promise<ForgeOAuthApp> {
  // Body type-checked against the spec's forgeOAuthAppReq.
  return (await apiPost("/api/teams/{id}/forge/oauth-apps", {
    params: { id: teamID },
    body: input,
  })) as ForgeOAuthApp;
}

export async function deleteForgeOAuthApp(teamID: string, appID: string): Promise<void> {
  await request<void>(`/teams/${teamID}/forge/oauth-apps/${appID}`, { method: "DELETE" });
}

// Platform forge OAuth apps (super-admin): one registration of a forge
// instance's application serves every team's connect flow; a team listing
// carries them with `platform: true`. Mirrors admin_forge_routes.go.
export interface PlatformForgeOAuthAppInput {
  provider: ForgeProvider;
  forge_base_url?: string;
  client_id: string;
  client_secret: string;
  webhook_base_url?: string;
}

export async function listPlatformForgeOAuthApps(): Promise<ForgeOAuthApp[]> {
  const r = await request<{ apps: ForgeOAuthApp[] }>("/admin/forge/oauth-apps");
  return r.apps ?? [];
}

export async function registerPlatformForgeOAuthApp(
  input: PlatformForgeOAuthAppInput,
): Promise<ForgeOAuthApp> {
  return request<ForgeOAuthApp>("/admin/forge/oauth-apps", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deletePlatformForgeOAuthApp(appID: string): Promise<void> {
  await request<void>(`/admin/forge/oauth-apps/${encodeURIComponent(appID)}`, {
    method: "DELETE",
  });
}

export interface GitHubManifestStart {
  /** github.com (or GHE) URL to POST the manifest form to. */
  post_url: string;
  /** The GitHub App manifest to submit as a hidden `manifest` form field. */
  manifest: Record<string, unknown>;
  state: string;
}

// startGitHubManifest returns the pre-filled GitHub App manifest + the
// github.com POST target; the caller auto-submits a form so GitHub creates the
// App and redirects back to iterion's callback (which stores the credentials).
// getMyGitHubOrgs returns the caller's GitHub org logins (captured via the
// read:org picker below or at GitHub SSO login) — UI hints for the org dropdown.
export async function getMyGitHubOrgs(): Promise<string[]> {
  const r = await request<{ orgs: string[] }>("/me/github-orgs");
  return r.orgs ?? [];
}

// startGitHubOrgsPicker returns a GitHub authorize URL (read:org via the global
// OAuth app); the studio redirects there. On return the orgs are persisted, so
// getMyGitHubOrgs then populates the dropdown.
export async function startGitHubOrgsPicker(next: string): Promise<{ authorize_url: string }> {
  return request(`/forge/github/orgs/start?next=${encodeURIComponent(next)}`);
}

export async function startGitHubManifest(
  teamID: string,
  // github_org creates the App UNDER that org (installable org-wide); blank =
  // the caller's personal account (then only installable there).
  // allow_repo_creation requests administration:write on the App so iterion
  // can CREATE repositories (opt-in, surfaced as a visible checkbox).
  // allow_app_delivery requests workflows:write + packages:write so a bot can
  // publish the CI that builds an app and the image it produces — without
  // them GitHub refuses any push touching .github/workflows/**.
  // allow_security_read requests vulnerability_alerts:read so a bot can read
  // the org's Dependabot alerts (docs/forge-security-read.md); off by default
  // and only ever minted into a dedicated token.
  // allow_project_board requests organization_projects:write so a team can
  // bind a GitHub Projects v2 board to its native board (docs/github-board-
  // sync.md); off by default and only ever minted per board call.
  // security_read_only builds a WATCH-ONLY App instead: metadata + Dependabot
  // alerts, both read, and nothing else — the shape meant to be installed on
  // ALL repositories without granting write anywhere. It replaces the runtime
  // permissions rather than adding to them, so it cannot be combined with the
  // allow_* options above.
  input: {
    forge_base_url?: string;
    next?: string;
    github_org?: string;
    allow_repo_creation?: boolean;
    allow_app_delivery?: boolean;
    allow_security_read?: boolean;
    allow_project_board?: boolean;
    security_read_only?: boolean;
  },
): Promise<GitHubManifestStart> {
  // Body type-checked against the spec's forgeOAuthAppReq (provider is
  // required there; this endpoint is GitHub-only, so state it explicitly).
  return (await apiPost("/api/teams/{id}/forge/oauth-apps/github-manifest", {
    params: { id: teamID },
    body: { provider: "github", ...input },
  })) as GitHubManifestStart;
}

// The three stable codes pkg/server answers for a launch_vars refusal of the
// repo-bots routes (forge_provisioning_routes.go). A surface branches on
// these, never on the message.
export const LAUNCH_VARS_DROP_CODE = "launch_vars_drop";
export const PROVISION_DIVERGED_CODE = "provision_diverged";
export const PROVISION_DIVERGED_RETRY_CODE = "provision_diverged_retry";

export type LaunchVarsRefusalKind = "drop" | "diverged" | "retry";

// LaunchVarsRefusal is a provisioning refusal whose remedy is a gesture the
// operator can make: echo the named pins, replace the whole map, or simply
// re-send the same request.
export interface LaunchVarsRefusal {
  kind: LaunchVarsRefusalKind;
  // keys are the pins the refusal named. EMPTY on a `diverged`/`retry`
  // refusal: a 500's payload is floored to its code (internal/httpx), so the
  // server's list does not travel — the surface names the pins it holds
  // itself rather than claiming the server said nothing was at stake.
  keys: string[];
  // message is the server's own words, "" when the floor dropped them.
  message: string;
}

// launchVarsRefusalOf reads a launch_vars refusal off a thrown error, or null
// when the failure is any other kind. The kind comes from the stable
// error_code and the keys from the envelope's `launch_vars_keys` — the typed
// cause the server put there — so a reworded message never changes which
// gesture is offered.
export function launchVarsRefusalOf(err: unknown): LaunchVarsRefusal | null {
  if (!(err instanceof ApiError)) return null;
  const kind: LaunchVarsRefusalKind | null =
    err.errorCode === LAUNCH_VARS_DROP_CODE
      ? "drop"
      : err.errorCode === PROVISION_DIVERGED_CODE
        ? "diverged"
        : err.errorCode === PROVISION_DIVERGED_RETRY_CODE
          ? "retry"
          : null;
  if (!kind) return null;
  const raw = err.body?.launch_vars_keys;
  const keys = Array.isArray(raw) ? raw.filter((k): k is string => typeof k === "string") : [];
  return { kind, keys, message: err.detail ?? "" };
}
